import { useEffect, useState, useCallback } from "react";
import { FileStack, FileText, BookOpen, GraduationCap, Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { STAFF_FORMS, SOP, LOG_TYPES } from "../data/staffFormsSchema";
import { StaffField } from "./FormRenderer";
import CaseFormDialog from "./CaseFormDialog";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

function SopDialog({ open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-surface border-line max-w-3xl max-h-[85vh] overflow-auto" data-testid="sop-dialog">
        <DialogHeader><DialogTitle className="font-serif text-navy">08 — {SOP.title}</DialogTitle></DialogHeader>
        <div className="space-y-8 mt-2">
          <div>
            <h3 className="text-sm font-semibold text-teal mb-3">1. Matter folder structure</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {SOP.folders.map((f) => <code key={f} className="text-xs bg-ivory border border-line rounded-md px-2.5 py-1.5 text-ink">{f}</code>)}
            </div>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-teal mb-2">2. File naming standard</h3>
            <code className="block text-xs bg-ivory border border-line rounded-md px-3 py-2 text-ink">{SOP.naming}</code>
            <ul className="mt-3 space-y-1.5">
              {SOP.namingRules.map((r) => <li key={r} className="text-xs text-mutedink flex gap-2"><span className="text-teal">✓</span>{r}</li>)}
            </ul>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-teal mb-3">3. Workflow stages</h3>
            <div className="overflow-x-auto rounded-xl border border-line">
              <table className="w-full text-xs">
                <thead><tr className="bg-ivory text-left">{["Stage", "Owner", "Required output", "Attorney gate?"].map((h) => <th key={h} className="px-3 py-2 font-semibold text-navy">{h}</th>)}</tr></thead>
                <tbody>
                  {SOP.stages.map((s) => (
                    <tr key={s[0]} className="border-t border-line">
                      {s.map((c, i) => <td key={i} className={`px-3 py-2 ${i === 3 && c.startsWith("YES") ? "text-champagne-dark font-semibold" : "text-ink"}`}>{c}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-teal mb-3">4. Quality control before attorney review</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {SOP.qcChecklist.map((q) => <div key={q} className="flex items-center gap-2 text-xs text-ink bg-ivory border border-line rounded-md px-3 py-2"><span className="text-sage">✓</span>{q}</div>)}
            </div>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-teal mb-2">5. Confidentiality &amp; access</h3>
            <p className="text-xs text-mutedink leading-relaxed">{SOP.confidentiality}</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TrainingLogs() {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [logType, setLogType] = useState("training");
  const [addOpen, setAddOpen] = useState(false);
  const [fields, setFields] = useState({});

  const load = useCallback(() => {
    api.get("/training-logs").then((r) => setLogs(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const activeType = LOG_TYPES.find((t) => t.id === logType);
  const filtered = logs.filter((l) => l.log_type === logType);

  const addEntry = async () => {
    if (!fields.date) { toast.error("Date is required."); return; }
    try {
      await api.post("/training-logs", { log_type: logType, fields });
      toast.success("Log entry added.");
      setAddOpen(false);
      setFields({});
      load();
    } catch { toast.error("Could not add entry."); }
  };

  const remove = async (log) => {
    try {
      await api.delete(`/training-logs/${log.log_id}`);
      load();
    } catch { toast.error("Could not delete entry."); }
  };

  return (
    <div className="mt-12" data-testid="training-logs-section">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-serif font-semibold text-navy flex items-center gap-2"><GraduationCap className="h-5 w-5 text-teal" /> 09 — DOJ R&amp;A Training, Experience &amp; Supervision Log</h2>
          <p className="text-xs text-mutedink mt-1">EOIR evaluates experience on duration, frequency, recency, percentage of immigration-law work, and meaningfulness of supervision. Keep verifiable records.{user?.role !== "administrator" && " You see your own entries."}</p>
        </div>
        <Button onClick={() => { setFields({}); setAddOpen(true); }} size="sm" className="bg-teal text-white hover:bg-teal-dark rounded-full" data-testid="add-log-entry-button"><Plus className="mr-2 h-4 w-4" /> Add entry</Button>
      </div>

      <Tabs value={logType} onValueChange={setLogType}>
        <TabsList className="bg-surface border border-line rounded-xl" data-testid="log-type-tabs">
          {LOG_TYPES.map((t) => <TabsTrigger key={t.id} value={t.id} className="text-xs data-[state=active]:bg-teal data-[state=active]:text-white rounded-lg" data-testid={`log-tab-${t.id}`}>{t.title}</TabsTrigger>)}
        </TabsList>
        {LOG_TYPES.map((t) => (
          <TabsContent key={t.id} value={t.id} className="mt-4">
            {filtered.length === 0 ? (
              <p className="text-sm text-mutedink bg-surface border border-dashed border-line rounded-2xl p-8 text-center">No {t.title.toLowerCase()} entries yet.</p>
            ) : (
              <div className="bg-surface border border-line rounded-2xl divide-y divide-line">
                {filtered.map((l) => (
                  <div key={l.log_id} className="p-4 flex items-start gap-3" data-testid={`log-row-${l.log_id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-navy">{l.fields.training || l.fields.matter_type || l.fields.court_matter || "Entry"} <span className="text-xs text-mutedink font-normal">· {l.fields.date}</span></p>
                      <p className="text-xs text-mutedink mt-0.5">{l.fields.topics || l.fields.task || l.fields.notes || ""}</p>
                      <p className="text-[11px] text-mutedink mt-1">by {l.staff_name}{l.fields.hours && ` · ${l.fields.hours} hrs`}{l.fields.supervisor && ` · supervised by ${l.fields.supervisor}`}</p>
                    </div>
                    <button onClick={() => remove(l)} className="p-1.5 text-mutedink hover:text-red-300 transition-colors" aria-label="Delete entry" data-testid={`log-delete-${l.log_id}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-surface border-line max-w-xl max-h-[85vh] overflow-auto" data-testid="log-entry-dialog">
          <DialogHeader><DialogTitle className="font-serif text-navy">Add {activeType.title} entry</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
            {activeType.fields.map((f) => (
              <StaffField key={f.key} field={f} value={fields[f.key]} onChange={(k, v) => setFields((p) => ({ ...p, [k]: v }))} />
            ))}
          </div>
          <Button onClick={addEntry} className="mt-6 bg-teal text-white hover:bg-teal-dark rounded-full" data-testid="log-entry-save">Save entry</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function StaffForms() {
  const [clients, setClients] = useState([]);
  const [cases, setCases] = useState([]);
  const [forms, setForms] = useState([]);
  const [picker, setPicker] = useState(null);
  const [pickerClient, setPickerClient] = useState("");
  const [pickerCase, setPickerCase] = useState("");
  const [editor, setEditor] = useState(null);
  const [sopOpen, setSopOpen] = useState(false);

  const load = useCallback(() => {
    api.get("/staff/clients").then((r) => setClients(r.data)).catch(() => {});
    api.get("/cases").then((r) => setCases(r.data)).catch(() => {});
    api.get("/case-forms").then((r) => setForms(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const clientCases = cases.filter((c) => c.client_id === pickerClient);

  const startForm = () => {
    if (!pickerCase) { toast.error("Choose a case first."); return; }
    setEditor({ formType: picker.id, caseId: pickerCase, existing: null });
    setPicker(null);
  };

  const deleteForm = async (f) => {
    try {
      await api.delete(`/case-forms/${f.form_id}`);
      toast.success("Form deleted.");
      load();
    } catch { toast.error("Could not delete form."); }
  };

  return (
    <div className="space-y-8" data-testid="staff-forms-page">
      <div>
        <h1 className="text-2xl font-serif font-semibold text-navy tracking-tight flex items-center gap-2"><FileStack className="h-6 w-6 text-teal" /> Forms Library</h1>
        <p className="mt-1 text-sm text-mutedink">The Full Circle Immigration Case Management System — fill case forms digitally, follow the workflow SOP, and log training &amp; supervision.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {STAFF_FORMS.map((f) => (
          <button key={f.id} onClick={() => { setPicker(f); setPickerClient(""); setPickerCase(""); }} className="bg-surface border border-line rounded-xl p-5 text-left hover:border-teal hover:shadow-md transition-all group" data-testid={`form-card-${f.id}`}>
            <div className="flex items-start justify-between">
              <span className="text-[10px] font-bold tracking-widest text-teal">FORM {f.id}</span>
              <FileText className="h-4 w-4 text-mutedink group-hover:text-teal transition-colors" />
            </div>
            <p className="mt-2 text-sm font-sans font-semibold text-navy">{f.title}</p>
            <p className="mt-1 text-xs text-mutedink leading-relaxed">{f.desc}</p>
          </button>
        ))}
        <button onClick={() => setSopOpen(true)} className="bg-surface border border-line rounded-xl p-5 text-left hover:border-teal hover:shadow-md transition-all group" data-testid="form-card-08">
          <div className="flex items-start justify-between">
            <span className="text-[10px] font-bold tracking-widest text-champagne-dark">SOP 08</span>
            <BookOpen className="h-4 w-4 text-mutedink group-hover:text-teal transition-colors" />
          </div>
          <p className="mt-2 text-sm font-sans font-semibold text-navy">{SOP.title}</p>
          <p className="mt-1 text-xs text-mutedink leading-relaxed">{SOP.desc}</p>
        </button>
        <div className="bg-surface border border-line rounded-xl p-5" data-testid="form-card-09">
          <div className="flex items-start justify-between">
            <span className="text-[10px] font-bold tracking-widest text-sage">LOG 09</span>
            <GraduationCap className="h-4 w-4 text-mutedink" />
          </div>
          <p className="mt-2 text-sm font-sans font-semibold text-navy">DOJ R&amp;A Training / Experience / Supervision Log</p>
          <p className="mt-1 text-xs text-mutedink leading-relaxed">Scroll down to add and review log entries below.</p>
        </div>
      </div>

      <section data-testid="saved-forms-list">
        <h2 className="text-base font-sans font-semibold text-navy mb-4">Completed forms ({forms.length})</h2>
        {forms.length === 0 ? (
          <p className="text-sm text-mutedink bg-surface border border-dashed border-line rounded-2xl p-8 text-center">No forms completed yet. Choose a form above to start one for a client case.</p>
        ) : (
          <div className="bg-surface border border-line rounded-2xl divide-y divide-line">
            {forms.map((f) => (
              <div key={f.form_id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`saved-form-${f.form_id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-navy">{FORM_BY_ID_TITLE(f.form_type)} <span className="text-xs text-mutedink font-normal">· {f.client_name} · {f.case_title}</span></p>
                  <p className="text-xs text-mutedink mt-0.5">by {f.filled_by_name} · updated {new Date(f.updated_at).toLocaleString()}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setEditor({ formType: f.form_type, caseId: f.case_id, existing: f })} className="p-2 text-teal hover:text-teal-dark transition-colors" aria-label="Edit form" data-testid={`form-edit-${f.form_id}`}><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => deleteForm(f)} className="p-2 text-mutedink hover:text-red-300 transition-colors" aria-label="Delete form" data-testid={`form-delete-${f.form_id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <TrainingLogs />

      {/* Client/case picker */}
      <Dialog open={!!picker} onOpenChange={(o) => !o && setPicker(null)}>
        <DialogContent className="bg-surface border-line max-w-md" data-testid="form-picker-dialog">
          <DialogHeader><DialogTitle className="font-serif text-navy">Start: {picker?.title}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Client</Label>
              <Select value={pickerClient} onValueChange={(v) => { setPickerClient(v); setPickerCase(""); }}>
                <SelectTrigger className="mt-1.5" data-testid="form-picker-client"><SelectValue placeholder="Choose a client" /></SelectTrigger>
                <SelectContent>
                  {clients.map((c) => <SelectItem key={c.user_id} value={c.user_id}>{c.name} — {c.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Case</Label>
              <Select value={pickerCase} onValueChange={setPickerCase} disabled={!pickerClient}>
                <SelectTrigger className="mt-1.5" data-testid="form-picker-case"><SelectValue placeholder={pickerClient ? "Choose a case" : "Choose a client first"} /></SelectTrigger>
                <SelectContent>
                  {clientCases.map((c) => <SelectItem key={c.case_id} value={c.case_id}>{c.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={startForm} className="bg-teal text-white hover:bg-teal-dark rounded-full w-full" data-testid="form-picker-start">Open form</Button>
          </div>
        </DialogContent>
      </Dialog>

      {editor && (
        <CaseFormDialog
          open={!!editor}
          onOpenChange={(o) => !o && setEditor(null)}
          formType={editor.formType}
          caseId={editor.caseId}
          existing={editor.existing}
          onSaved={load}
        />
      )}

      <SopDialog open={sopOpen} onOpenChange={setSopOpen} />
    </div>
  );
}

function FORM_BY_ID_TITLE(id) {
  const f = STAFF_FORMS.find((x) => x.id === id);
  return f ? `${f.id} — ${f.title}` : `Form ${id}`;
}
